import { Injectable, OnDestroy } from '@angular/core';
import { isNotNullOrUndefined } from '@mri-platform/shared/core';
import { AuthService } from '@mri/angular-wfe-proxy-oidc';
import {
  BehaviorSubject,
  combineLatest,
  distinctUntilChanged,
  firstValueFrom,
  map,
  Observable,
  Subscription
} from 'rxjs';

interface UserDetails {
  userId: string;
  clientId: string;
}

interface CacheEntry<T = unknown> {
  value: T;
  expiresAt?: number;
}

interface GetOptions<T> {
  throwOnUserNotLoaded: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  reader: (value: any) => T;
}

interface SetOptions<T = unknown> {
  ttl: number;
  writer: (value: T) => unknown;
}

const userNotLoadedError = 'User not loaded';

const identity = (value: unknown): unknown => value;

const defaultGetOptions: GetOptions<unknown> = {
  reader: identity,
  throwOnUserNotLoaded: true
};

const defaultSetOptions: SetOptions = {
  ttl: 0, // no expiration
  writer: identity
};

@Injectable({ providedIn: 'root' })
export class UserScopedCacheService implements OnDestroy {
  private readonly subscription = new Subscription();
  private readonly userDetailsSub = new BehaviorSubject<UserDetails | undefined>(undefined);
  /**
   * Observable stream of user details
   * This stream is used as a caching scope identifier that changes whenever the user/client changes.
   */
  scope$ = this.userDetailsSub.pipe(isNotNullOrUndefined());

  constructor(private readonly authService: AuthService) {
    this.subscription = this.createGetUserDetails$().subscribe(user => {
      this.userDetailsSub.next(user);
    });
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
  }

  /**
   * Retrieves an item from the cache by key
   * @param key The key to retrieve from the cache
   * @param options Optional configuration for retrieving the item
   * @param options.throwOnUserNotLoaded When true (default), throws an error if the user is not loaded.
   *                                    When false, returns null instead.
   * @param options.reader Custom function to read the value from storage into the desired type.
   *                      Default is simply returns the value as is since it's already rehydrated.
   * @returns The cached value or null if not found
   * @throws Error if user is not loaded and throwOnUserNotLoaded is true
   */
  getItem<T>(key: string, options?: Partial<GetOptions<T>>): T | null {
    const { throwOnUserNotLoaded = defaultGetOptions.throwOnUserNotLoaded } = options ?? defaultGetOptions;
    const userDetails = this.verifyUserLoaded(throwOnUserNotLoaded);
    if (!userDetails) return null;
    return this.getItemFromCache<T>(key, options);
  }

  /**
   * Asynchronously retrieves an item from the cache by key
   * @param key The key to retrieve from the cache
   * @param options Optional configuration for retrieving the item
   * @param options.throwOnUserNotLoaded Not used in async version as it waits for user to be loaded
   * @param options.reader Custom function to read the value from storage into the desired type.
   *                      Default returns the value "as is" since it's already rehydrated from storage where it was stored as JSON.
   * @returns Promise that resolves to the cached value or null if not found
   */
  async getItemAsync<T>(key: string, options?: Partial<GetOptions<T>>): Promise<T | null> {
    await this.ensureUserLoaded();
    return this.getItemFromCache<T>(key, options);
  }

  /**
   * Checks if an item exists in the cache by key
   * @param key The key to check in the cache
   * @returns boolean indicating whether the item exists in the cache
   * @throws Error if user is not loaded
   */
  hasItem(key: string): boolean {
    this.verifyUserLoaded();
    return this.hasItemInCache(key);
  }

  /**
   * Asynchronously checks if an item exists in the cache by key
   * @param key The key to check in the cache
   * @returns Promise that resolves to a boolean indicating whether the item exists in the cache
   */
  async hasItemAsync(key: string): Promise<boolean> {
    await this.ensureUserLoaded();
    return this.hasItemInCache(key);
  }

  /**
   * Removes an item from the cache by key
   * @param key The key of the item to remove from the cache
   * @throws Error if user is not loaded, as the cache is user-scoped
   */
  removeItem(key: string): void {
    this.verifyUserLoaded();
    return this.removeItemFromCache(key);
  }

  /**
   * Asynchronously removes an item from the cache by key
   * @param key The key of the item to remove from the cache
   * @returns Promise that resolves when the item is removed
   * @remarks This method waits for the user to be loaded before attempting to remove the item
   */
  async removeItemAsync(key: string): Promise<void> {
    await this.ensureUserLoaded();
    return this.removeItemFromCache(key);
  }

  /**
   * Removes an array of items from the cache by key
   * @param keys The keys of the item to remove from the cache
   * @throws Error if user is not loaded, as the cache is user-scoped
   */
  removeItems(...keys: string[]) {
    this.verifyUserLoaded();
    return keys.map(this.removeItem.bind(this));
  }

  /**
   * Asynchronously removes an array of items from the cache by key
   * @param keys The keys of the items to remove from the cache
   * @returns Promise that resolves when the item is removed
   * @remarks This method waits for the user to be loaded before attempting to remove the item
   */
  async removeItemsAsync(...keys: string[]) {
    await this.ensureUserLoaded();
    return Promise.all(keys.map(this.removeItemAsync.bind(this)));
  }

  /**
   * Stores an item in the cache with the specified key
   * @param key The key to store the value under
   * @param value The value to store in the cache
   * @param options Optional configuration for storing the item
   * @param options.ttl Time-to-live in milliseconds. Default is 0 (no expiration).
   *                   When set, the item will automatically expire after the specified time.
   * @param options.writer Custom function to convert the value to the desired type before it's serialized for storage.
   *                       By default, returns value "as is".
   * @throws Error if user is not loaded
   * @remarks undefined values will be cached as null
   */
  setItem<T>(key: string, value: T, options?: Partial<SetOptions<T>>): void {
    this.verifyUserLoaded();
    return this.setItemInCache<T>(key, value, options);
  }

  /**
   * Asynchronously stores an item in the cache with the specified key
   * @param key The key to store the value under
   * @param value The value to store in the cache
   * @param options Optional configuration for storing the item
   * @param options.ttl Time-to-live in milliseconds. Default is 0 (no expiration).
   *                   When set, the item will automatically expire after the specified time.
   * @param options.writer Custom function to convert the value to the desired type before it's serialized for storage.
   *                       By default, returns value "as is".
   * @returns Promise that resolves when the item is stored
   * @remarks undefined values will be cached as null
   */
  async setItemAsync<T>(key: string, value: T, options?: Partial<SetOptions>): Promise<void> {
    await this.ensureUserLoaded();
    return this.setItemInCache<T>(key, value, options);
  }

  private async ensureUserLoaded(): Promise<void> {
    await firstValueFrom(this.userDetailsSub.pipe(isNotNullOrUndefined()));
  }

  private createGetUserDetails$(): Observable<UserDetails> {
    const userId$ = this.authService.userLoaded$.pipe(
      map(user => user['id']?.toString()),
      isNotNullOrUndefined(),
      distinctUntilChanged()
    );
    const clientId$ = this.authService.currentClientId$.pipe(isNotNullOrUndefined());
    return combineLatest({
      userId: userId$,
      clientId: clientId$
    });
  }

  private getCacheEntry<T>(key: string): CacheEntry<T> | null {
    const storageKey = this.getPrefixedKey(key);
    const raw = localStorage.getItem(storageKey);
    if (raw === null) return null;

    // if the item is not a valid JSON, then someone has tampered with the cache as we always store JSON strings
    // therefore we remove the corrupted cached entry and return null as if this was a cache miss
    let entry: CacheEntry;
    try {
      entry = JSON.parse(raw) as CacheEntry;
    } catch {
      localStorage.removeItem(storageKey);
      return null;
    }

    // if the entry DOES have an `expiresAt` property that is NOT a number then we have a corrupted cache entry
    // which we need to remove and return null as if this was a cache miss
    // noinspection SuspiciousTypeOfGuard
    if (entry.expiresAt !== undefined && (typeof entry.expiresAt !== 'number' || entry.expiresAt < Date.now())) {
      localStorage.removeItem(storageKey);
      return null;
    }

    return entry as CacheEntry<T> | null;
  }

  private getItemFromCache<T>(key: string, options?: Partial<GetOptions<T>>): T | null {
    const { reader = defaultGetOptions.reader } = options ?? defaultGetOptions;
    const entry = this.getCacheEntry<T>(key);
    if (!entry) return null;
    return (reader(entry.value) ?? null) as T | null;
  }

  private getPrefixedKey(key: string): string {
    const { clientId, userId } = this.userDetailsSub.value || {};
    return `${clientId}:${userId}:${key}`;
  }

  private hasItemInCache(key: string): boolean {
    return this.getCacheEntry(key) !== null;
  }

  private removeItemFromCache(key: string): void {
    const storageKey = this.getPrefixedKey(key);
    localStorage.removeItem(storageKey);
  }

  private setItemInCache<T>(key: string, value: T, options?: Partial<SetOptions<T>>): void {
    const { ttl = defaultSetOptions.ttl, writer = defaultSetOptions.writer } = options ?? defaultSetOptions;
    const storageKey = this.getPrefixedKey(key);
    const entry: CacheEntry = {
      value: writer(value) ?? null,
      expiresAt: ttl ? Date.now() + ttl : undefined
    };
    localStorage.setItem(storageKey, JSON.stringify(entry));
  }

  private verifyUserLoaded(throwOnUserNotLoaded = true) {
    const userDetails = this.userDetailsSub.value;
    if (!userDetails && throwOnUserNotLoaded) throw new Error(userNotLoadedError);
    return userDetails;
  }
}
